import { CashTypeEnum } from "@/model/data/enums/CashTypeEnum";
import { LicenseModuleEnum } from "@/model/data/License/LicenseModuleEnum";
import { InstallationPredictedStatusEnum } from "@/model/data/predictiveMaintenance/InstallationPredictedStatusEnum";
import { ConnectivityStatusEnum } from "@/model/data/status/ConnectivityStatusEnum";
import { InstallationStatusEnum } from "@/model/data/status/InstallationStatusEnum";
import { StatusEnum } from "@/model/data/status/StatusEnum";

export const Brand_Blue = "#008ffb";
export const Brand_Green = "#00e396";
export const Brand_Magenta = "#ff4560";
export const Brand_Yellow = "#feb019";
export const Brand_Purple = "#775DD0";
export const Brand_DarkBlue = "#3F51B5";
export const Brand_Red = "#EA3546";
export const Brand_Turquoise = "#26a69a";

export const Charts_Palette = [Brand_Blue, Brand_Green, Brand_Yellow, Brand_Magenta, Brand_Purple, "#546E7A", "#26a69a", "#D10CE8"];

export const MODULE_COLORS = {
    SERVICE_AND_UPDATES: Brand_Blue,
    CASH_FLOW: Brand_Green,
    FITNESS: Brand_Magenta,
    SECURITY_AND_CONFIGURATION: Brand_Yellow,
    PREDICTIVE_MAINTENANCE: Brand_Purple,
    REMOTE_LINK: Brand_DarkBlue,
    ONE_TIME_CODE: Brand_Red,
    DATAFLOW: Brand_Turquoise,
};

export const DEVICE_TREE_CONSTANTS = {
    FOLDER_COLOR: Brand_Yellow,
    DEVICE_COLOR: Brand_Green,
};

export function getLicenseModuleIconColor(module: LicenseModuleEnum) {
    switch (module) {
        case LicenseModuleEnum.ServiceAndUpdatesManagement:
            return {
                icon: "globe",
                color: MODULE_COLORS.SERVICE_AND_UPDATES,
            };
        case LicenseModuleEnum.CashFlowManagement:
            return {
                icon: "money-bill-wave",
                color: MODULE_COLORS.CASH_FLOW,
            };
        case LicenseModuleEnum.FitnessStatistics:
            return {
                icon: "table",
                color: MODULE_COLORS.FITNESS,
            };
        case LicenseModuleEnum.SecurityManagement:
            return {
                icon: "unlock-alt",
                color: MODULE_COLORS.SECURITY_AND_CONFIGURATION,
            };
        case LicenseModuleEnum.PredictiveMaintenance:
            return {
                icon: "server",
                color: MODULE_COLORS.PREDICTIVE_MAINTENANCE,
            };
        case LicenseModuleEnum.RemoteLink:
            return {
                icon: "link",
                color: MODULE_COLORS.REMOTE_LINK,
            };
        case LicenseModuleEnum.OneTimeCode:
            return {
                icon: "key",
                color: MODULE_COLORS.ONE_TIME_CODE,
            };
        case LicenseModuleEnum.DataFlow:
            return {
                icon: "hdd",
                color: MODULE_COLORS.DATAFLOW,
            };
        case LicenseModuleEnum.None:
            return {
                icon: "tools",
                color: "gray",
            };
        default:
            break;
    }
}

export function getInstallationStatusIconColor(status: InstallationStatusEnum) {
    switch (status) {
        case InstallationStatusEnum.Ok:
            return {
                icon: "check",
                color: "success",
            };
        case InstallationStatusEnum.Warning:
            return {
                icon: "exclamation-triangle",
                color: "warning",
            };
        case InstallationStatusEnum.PartiallyBlocked:
            return {
                icon: "exclamation",
                color: "warning",
            };
        case InstallationStatusEnum.Blocked:
            return {
                icon: "exclamation-circle",
                color: "error",
            };
        case InstallationStatusEnum.Unknown:
            return {
                icon: "question",
                color: "black",
            };
        default:
            break;
    }
}

export function getInstallationConnectivityStatusIconColor(status: ConnectivityStatusEnum) {
    switch (status) {
        case ConnectivityStatusEnum.Ok:
            return {
                icon: "link",
                color: "success",
            };
        case ConnectivityStatusEnum.AgentOffline:
        case ConnectivityStatusEnum.ApplicationOffline:
            return {
                icon: "plug",
                color: "warning",
            };
        case ConnectivityStatusEnum.Offline:
            return {
                icon: "unlink",
                color: "error",
            };
        case ConnectivityStatusEnum.Unknown:
            return {
                icon: "question",
                color: "black",
            };
        default:
            break;
    }
}

export function getInstallationPredictedStatusIconColor(status: InstallationPredictedStatusEnum) {
    switch (status) {
        case InstallationPredictedStatusEnum.Ok:
            return {
                icon: "check",
                color: "success",
            };
        case InstallationPredictedStatusEnum.Failure:
            return {
                icon: "exclamation-circle",
                color: "error",
            };
        case InstallationPredictedStatusEnum.Warning:
            return {
                icon: "exclamation-triangle",
                color: "warning",
            };
        default:
            return {
                icon: "check",
                color: "success",
            };
    }
}

export function getStatusIconColor(status: StatusEnum) {
    switch (status) {
        case StatusEnum.Online:
            return {
                icon: "check",
                color: "success",
            };
        case StatusEnum.Busy:
            return {
                icon: "exclamation-triangle",
                color: "warning",
            };
        case StatusEnum.Full:
            return {
                icon: "exclamation-triangle",
                color: "warning",
            };
        case StatusEnum.OutOfService:
            return {
                icon: "exclamation-circle",
                color: "error",
            };
        case StatusEnum.Offline:
            return {
                icon: "plug",
                color: "error",
            };
        default:
            break;
    }
}

const InLaneModelsList = ["InLane IA.300.NN", "InLane IA.300.00", "InLane IB.300.00", "InLane IB.300.C0", "InLane IM.000.00", "InLane CCR", "InLane IB.300", "InLane 300"];
const InLaneDepoModelsList = ["InLane IK.300.CC", "InLane IA.300.CC", "InLane IA.300", "InLane 300S", "InLane CCR Depo Plus", "InLane 300C", "InLane CCR Depo"];

function getImageFromModel(model, pixel, supportedCashType?: CashTypeEnum) {
    let cashType = "";
    if (supportedCashType && supportedCashType == CashTypeEnum.Banknote) cashType = "B";
    else if (supportedCashType == CashTypeEnum.Coin) cashType = "C";

    if (!model) return "";
    if (model.includes("CDS 707")) return `assets/images/devices/CDS707_@${pixel}.png`;
    if (model.includes("CDS 801") || model.includes("CDS-801")) return `assets/images/devices/CDS801_@${pixel}.png`;
    if (model.includes("CDS 802")) return `assets/images/devices/CDS802_@${pixel}.png`;
    if (model.includes("CDS 803")) return `assets/images/devices/CDS803_@${pixel}.png`;
    if (model.includes("CDS 804")) return `assets/images/devices/CDS803_@${pixel}.png`;
    if (model.includes("CDS 508")) return `assets/images/devices/CDS508_@${pixel}.png`;
    if (model.includes("CDS")) return `assets/images/devices/CDS803_@${pixel}.png`;

    if (model.includes("7000")) return `assets/images/devices/7000_@${pixel}.png`;
    if (model.includes("100") && model.toUpperCase().includes("MAXI")) return `assets/images/devices/SDM100M_@${pixel}.png`;
    if (model.includes("100")) return `assets/images/devices/SDM100_@${pixel}.png`;
    if (model.includes("7008") || model.includes("7016") || model.includes("7012")) return `assets/images/devices/7008_@${pixel}.png`;
    if (model.includes("SDM 55")) return cashType ? `assets/images/devices/SDM55_${cashType}_@${pixel}.png` : `assets/images/devices/SDM55_B_@${pixel}.png`;
    if (model === "SDM 50S") return `assets/images/devices/SDM50S_@${pixel}.png`;
    if (model === "SDM 10C" || model === "SDM 10XS") return `assets/images/devices/SDM10C_@${pixel}.png`;
    if (model.includes("SDM 7")) return `assets/images/devices/SDM7_@${pixel}.png`;

    if (model.includes("SDM")) return `assets/images/devices/SDM_@${pixel}.png`;
    if (model.includes("CasHere")) return `assets/images/devices/CasHere_@${pixel}.png`;
    if (model.includes("AST 9000")) return `assets/images/devices/AST9000_@${pixel}.png`;

    if (InLaneDepoModelsList.includes(model))
        return cashType != "" ? `assets/images/devices/InLane300Depo_${cashType}_@${pixel}.png` : `assets/images/devices/InLane300Depo_@${pixel}.png`;
    if (InLaneModelsList.includes(model) || model.includes("InLane"))
        return cashType != "" ? `assets/images/devices/InLane300_${cashType}_@${pixel}.png` : `assets/images/devices/InLane300_@${pixel}.png`;
    if (model == "AST") return `assets/images/devices/7008_@${pixel}.png`;
    if (model == "DropVault") return `assets/images/devices/DropVault_@${pixel}.png`;

    return "";
}

export function getImageFromModel_48(model, supportedCashType?) {
    return supportedCashType != null ? getImageFromModel(model, 48, supportedCashType) : getImageFromModel(model, 48);
}

export function getImageFromModel_200(model, supportedCashType?) {
    return supportedCashType ? getImageFromModel(model, 200, supportedCashType) : getImageFromModel(model, 200);
}

export function compareSoftwareVersions(softwareA, softwareB) {
    let softwareVersionA = softwareA.version
        .split(".")
        .map((n) => +n + 100000)
        .join(".");
    let softwareVersionB = softwareB.version
        .split(".")
        .map((n) => +n + 100000)
        .join(".");
    if (softwareVersionA < softwareVersionB) return -1;
    if (softwareVersionA > softwareVersionB) return 1;
    return 0;
}

export const dataTableItemsPerPage = [20, 30, 50, 100];
export const notificationsDataTableItemsPerPage = [10, 15];

export const currencies = [
    "AED",
    "AFN",
    "ALL",
    "AMD",
    "ANG",
    "AOA",
    "ARS",
    "AUD",
    "AWG",
    "AZN",
    "BAM",
    "BBD",
    "BDT",
    "BGN",
    "BHD",
    "BIF",
    "BMD",
    "BND",
    "BOB",
    "BRL",
    "BSD",
    "BTN",
    "BWP",
    "BYN",
    "BZD",
    "CAD",
    "CDF",
    "CHF",
    "CLP",
    "CNY",
    "COP",
    "CRC",
    "CUP",
    "CVE",
    "CZK",
    "DJF",
    "DKK",
    "DOP",
    "DZD",
    "EGP",
    "ERN",
    "ETB",
    "EUR",
    "FJD",
    "FKP",
    "FOK",
    "GBP",
    "GEL",
    "GGP",
    "GHS",
    "GIP",
    "GMD",
    "GNF",
    "GTQ",
    "GYD",
    "HKD",
    "HNL",
    "HRK",
    "HTG",
    "HUF",
    "IDR",
    "ILS",
    "IMP",
    "INR",
    "IQD",
    "IRR",
    "ISK",
    "JEP",
    "JMD",
    "JOD",
    "JPY",
    "KES",
    "KGS",
    "KHR",
    "KID",
    "KMF",
    "KRW",
    "KWD",
    "KYD",
    "KZT",
    "LAK",
    "LBP",
    "LKR",
    "LRD",
    "LSL",
    "LYD",
    "MAD",
    "MDL",
    "MGA",
    "MKD",
    "MMK",
    "MNT",
    "MOP",
    "MRU",
    "MUR",
    "MVR",
    "MWK",
    "MXN",
    "MYR",
    "MZN",
    "NAD",
    "NGN",
    "NIO",
    "NOK",
    "NPR",
    "NZD",
    "OMR",
    "PAB",
    "PEN",
    "PGK",
    "PHP",
    "PKR",
    "PLN",
    "PYG",
    "QAR",
    "RON",
    "RSD",
    "RUB",
    "RWF",
    "SAR",
    "SBD",
    "SCR",
    "SDG",
    "SEK",
    "SGD",
    "SHP",
    "SLE",
    "SLL",
    "SOS",
    "SRD",
    "SSP",
    "STN",
    "SYP",
    "SZL",
    "THB",
    "TJS",
    "TMT",
    "TND",
    "TOP",
    "TRY",
    "TTD",
    "TVD",
    "TWD",
    "TZS",
    "UAH",
    "UGX",
    "USD",
    "UYU",
    "UZS",
    "VES",
    "VND",
    "VUV",
    "WST",
    "XAF",
    "XCD",
    "XOF",
    "XPF",
    "YER",
    "ZAR",
    "ZMW",
    "ZWL",
];
